import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { Card, Empty, StatusBadge } from "../panel/PanelShell";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { downloadReport } from "../../lib/report";

export function ConsultationsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/portal/consultations").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Your consultation requests and their status.</p>
        <Link to="/book-consultation" data-testid="portal-book-consultation-btn" className="rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy">Book new</Link>
      </div>
      {rows && !rows.length && <Empty text="No consultations yet." testid="consultations-empty" />}
      <div className="space-y-3">
        {(rows || []).map((c) => (
          <Card key={c.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`portal-consultation-${c.id}`}>
            <div><p className="font-semibold text-navy">{c.service}</p><p className="text-sm text-slate-500">Preferred: {fmtDate(c.preferred_date)} · Budget: {c.budget} · Requested {fmtDate(c.created_at)}</p></div>
            <StatusBadge status={c.status} />
          </Card>
        ))}
      </div>
    </div>
  );
}

const TYPES = { all: "All", investment_plan: "Investment Planning", insurance_comparison: "Insurance Comparison", mf_research: "Mutual Fund Research", general: "General" };

export function ReportsTab({ initialType = "all" }) {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState(initialType);
  useEffect(() => { api.get("/portal/reports").then((r) => setRows(r.data)); }, []);
  const shown = (rows || []).filter((r) => type === "all" || r.type === type);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {Object.entries(TYPES).map(([k, v]) => (
          <button key={k} onClick={() => setType(k)} data-testid={`reports-filter-${k}`} className={`rounded-full px-4 py-2 text-sm ${type === k ? "bg-navy text-white" : "bg-white text-slate-600"}`}>{v}</button>
        ))}
      </div>
      {rows && !shown.length && <Empty text="No reports yet. Reports prepared by your Quantora advisor will appear here." testid="reports-empty" />}
      <div className="grid gap-4 md:grid-cols-2">
        {shown.map((r) => (
          <Card key={r.id} data-testid={`report-card-${r.id}`}>
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-50 text-gold-600"><FileText size={19} /></div>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-mint-600">{TYPES[r.type]}</p>
                <h3 className="mt-1 font-bold text-navy">{r.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{r.summary}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                  <button onClick={() => downloadReport(r)} data-testid={`report-download-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-mint-600"><Download size={15} /> Download</button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const MF_CATEGORIES = [
  ["Large Cap", "Equity", "High", "5+ years", "Invests mainly in the top 100 companies by market capitalisation."],
  ["Flexi Cap", "Equity", "High", "5+ years", "Flexibility to invest across large, mid and small companies."],
  ["Mid & Small Cap", "Equity", "Very High", "7+ years", "Higher growth potential with significantly higher volatility."],
  ["ELSS", "Equity", "High", "3-year lock-in", "Equity scheme with tax benefits under applicable provisions."],
  ["Liquid", "Debt", "Low", "Days – 3 months", "Short-term money market instruments, suitable for parking funds."],
  ["Corporate Bond", "Debt", "Low – Moderate", "1 – 3 years", "Invests predominantly in high-rated corporate debt."],
  ["Balanced Advantage", "Hybrid", "Moderate – High", "3+ years", "Dynamically shifts between equity and debt."],
  ["Index Funds", "Equity (passive)", "High", "5+ years", "Tracks a market index at typically low cost."],
];

export function MfResearchTab() {
  return (
    <div className="space-y-8">
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="mf-research-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Category", "Asset class", "Risk", "Suggested horizon", "What it is"].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {MF_CATEGORIES.map((r) => <tr key={r[0]} className="border-t border-slate-100">{r.map((c, i) => <td key={i} className={`px-5 py-3 ${i === 0 ? "font-semibold text-navy" : "text-slate-600"}`}>{c}</td>)}</tr>)}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">Educational overview of SEBI-defined mutual fund categories. Not a recommendation. Mutual fund investments are subject to market risks, read all scheme related documents carefully.</p>
      <div><h3 className="mb-4 font-bold text-navy">Your mutual fund research reports</h3><ReportsTab initialType="mf_research" /></div>
    </div>
  );
}
